<template>
  <div id="layout-wrap">
    <v-navigation-drawer
      color="primary"
      dark
      :mini-variant="mini"
      :width="$vuetify.breakpoint.xsOnly ? 241 : 269"
      v-model="showDrawer"
      :mini-variant-width="70"
      :temporary="$vuetify.breakpoint.xsOnly"
      :permanent="!$vuetify.breakpoint.xsOnly"
      fixed
    >
      <div class="layout-logo d-flex justify-start align-center" @click="$router.push('/')">
        <img v-if="!mini" alt="logo" src="../../assets/logo/expanded.svg" />
        <img v-else alt="logo" src="../../assets/logo/reduced.svg" />
      </div>
      <v-divider />
      <v-list-item link @click="redirectToSettings" class="justify-start layout-nav-profile">
        <v-skeleton-loader v-if="isLoading" type="list-item-avatar" width="100%"></v-skeleton-loader>
        <template v-else>
          <v-list-item-avatar>
            <v-img v-if="photoURL" :src="photoURL" />
            <img v-else alt="no-avatar" src="../../assets/no-avatar.svg" />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="layout-nav-list-name">{{ displayName }}</v-list-item-title>
            <v-list-item-subtitle class="layout-nav-list-rolename">
              {{ roleName }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <v-btn icon>
          <img alt="tune.svg" src="../../assets/sidebar/tune.svg" />
        </v-btn>
      </v-list-item>
      <v-divider :class="{ 'layout-nav-divider-inset': mini }" class="layout-nav-divider" />
      <NavList
        :navTabs="navTabs"
        :plannedEncountersCount="plannedEncounters.length"
        :unreadMessagesCount="unreadMessagesCount"
      />
      <v-subheader v-if="!mini" class="layout-nav-subheader">{{ $t("general.sidebar.quickActions") }}</v-subheader>
      <v-divider v-else class="layout-nav-divider-quick-actions" />
      <NavList :navTabs="quickActions" @click="action" />
      <template v-slot:append>
        <v-list-item link @click="toggleSidebar" class="justify-start layout-nav-item-close" :ripple="false">
          <v-list-item-icon class="ml-7px" :class="{ 'collapsed-icon': mini }" v-if="!$vuetify.breakpoint.xsOnly">
            <img src="../../assets/sidebar/collapse.svg" alt="collapse.svg" />
          </v-list-item-icon>
          <v-list-item-content v-if="!$vuetify.breakpoint.xsOnly">
            <v-list-item-title class="layout-nav-item-title">
              {{ $t("general.sidebar.collapseSidebar") }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-divider />
        <v-list-item link @click="toggleDialog" class="justify-start">
          <v-list-item-icon class="ml-7px">
            <img src="../../assets/sidebar/logout.svg" alt="logout.svg" />
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="layout-nav-item-title">
              {{ $t("general.sidebar.logout") }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-navigation-drawer>
    <div
      class="layout-slot-wrapper"
      :class="{ 'layout-slot-wrapper--reduced': !mini, 'layout-slot-wrapper--expanded': mini }"
    >
      <Header />
      <slot />
      <Footer />
    </div>
    <InvitePatientDialog v-model="inviteDialog" @toggleDialog="toggleInviteDialog" />
    <Confirm
      :dialog="dialog"
      :title="$t('general.sidebar.logout')"
      :text="$t('general.sidebar.logoutConfirm')"
      :confirmText="$t('general.dialog.yes')"
      :cancelText="$t('general.dialog.cancel')"
      @toggleDialog="toggleDialog"
      @confirm="logOutMethod"
    />
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import InvitePatientDialog from "@/components/InvitePatientDialog/index";
import router from "@/router";

import Confirm from "../shared/Confirm.vue";
import Footer from "./Footer";
import Header from "./Header";
import NavList from "./NavList";

export default {
  name: "Layout",
  props: ["navTabs", "role", "quickActions"],
  components: {
    Header,
    InvitePatientDialog,
    NavList,
    Confirm,
    Footer,
  },
  updated() {
    if (this.allowingMode) {
      this.allowingMode = false;
      return;
    }
    this.adjustCollapsing();
  },
  mounted() {
    this.adjustCollapsing();
  },
  data: () => ({
    dialog: false,
    inviteDialog: false,
    internalMini: false,
    forbidCollapsing: false,
    allowingMode: false,
  }),
  computed: {
    ...mapGetters(["photoURL"]),
    ...mapGetters("encounters", ["plannedEncounters"]),
    ...mapGetters("chat", ["unreadMessagesCount"]),
    ...mapGetters(["drawer"]),
    ...mapGetters("profileSettings", ["practitionerSettings", "patientSettings", "ownerSettings", "registrarSettings"]),
    mini() {
      // mobile version do not have mini version
      if (this.$vuetify.breakpoint.xsOnly) {
        return false;
      }
      return this.internalMini;
    },

    showDrawer: {
      get() {
        return this.drawer;
      },
      set(value) {
        return this.setDrawer(value);
      },
    },

    isLoading() {
      return !this.setting.firstName;
    },

    setting() {
      let roleSetting = "";
      if (this.role === "practitioner") {
        roleSetting = "practitionerSettings";
      } else if (this.role === "patient") {
        roleSetting = "patientSettings";
      } else if (this.role === "owner") {
        roleSetting = "ownerSettings";
      } else if (this.role === "registrar") {
        roleSetting = "registrarSettings";
      }
      return this[roleSetting];
    },

    displayName() {
      return `${this.setting.firstName} ${this.setting.lastName}`;
    },

    roleName() {
      return this.role.charAt(0).toUpperCase() + this.role.slice(1);
    },
  },
  methods: {
    ...mapActions(["logOut"]),
    ...mapActions("encounters", ["getAllEncounters"]),
    ...mapMutations(["setDrawer"]),
    action(eventName) {
      if (this[eventName]) {
        this[eventName]();
      }
    },
    toggleDialog() {
      this.dialog = !this.dialog;
    },
    toggleInviteDialog() {
      this.inviteDialog = !this.inviteDialog;
    },
    toggleSidebar() {
      this.allowingMode = true;
      this.internalMini = !this.internalMini;
      localStorage.setItem("sidebarStatus", this.internalMini ? "collapsed" : "expanded");
    },
    adjustCollapsing() {
      if (this.$router.history.current.meta.collapseSidebar) {
        this.internalMini = true;
        this.forbidCollapsing = true;
      } else {
        this.internalMini = localStorage.getItem("sidebarStatus") === "collapsed";
        this.forbidCollapsing = false;
      }
    },
    async redirectToSettings() {
      await this.$router.push(`/${this.role}/settings`);
    },
    async logOutMethod() {
      await this.logOut();
      await router.push({ path: `/auth/signin` });
    },
  },
};
</script>

<style lang="scss">
#layout-wrap {
  height: 100%;
}
.layout-nav-profile {
  height: 72px;
}
.v-navigation-drawer {
  z-index: 2;
}
.layout-nav-divider-quick-actions {
  margin: 23px 21px 24px 21px;
}
.layout-nav-divider-inset {
  margin: 0 21px 0 21px;
}
.layout-nav-divider {
  margin-bottom: 18px;
}
.layout-nav-subheader {
  width: 300px;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.36px;
  text-transform: uppercase;
  color: #ffffff;
  mix-blend-mode: normal;
  opacity: 0.4;
}
.layout-nav-list-name {
  font-size: 17px;
  line-height: 20px;
  color: #ffffff;
}
.layout-nav-list-rolename {
  font-size: 13px;
  line-height: 16px;
  color: #ffffff;
  mix-blend-mode: normal;
  opacity: 0.4;
}
.collapsed-icon {
  transform: rotate(180deg);
}
.layout-logo {
  height: 72px;
  padding-left: 20px;
  cursor: pointer;
}
.layout-nav-item-title {
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #ffffff;
}
.layout-nav-item-close {
  opacity: 0.56;
}
.layout-slot-wrapper {
  transition: margin 200ms;
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;

  &--reduced {
    margin-left: 269px;
  }

  &--expanded {
    margin-left: 70px;
  }

  @media (max-width: 600px) {
    margin-left: 0 !important;
  }
}
</style>
